import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  QuestionLayout,
  RectangularOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useMenstrualHealth } from "../MenstrualHealthContext";
import { buildMenstrualHealthNavigationStack } from "../buildMenstrualHealthNavigationStack";
import type { MenstrualHealthStackParamList } from "../MenstrualHealthNavigator";
import { CYCLE_STATUS_OPTIONS } from "../options";
import type { CycleStatus } from "../options";

export function CycleStatusScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useMenstrualHealth();
  const navigation =
    useNavigation<NavigationProp<MenstrualHealthStackParamList>>();

  const handleSelect = (id: CycleStatus) => {
    const stateUpdates = { cycleStatus: id };
    updateState(stateUpdates);

    if (state.cycleStatus === null) {
      void save(stateUpdates).catch(() => undefined);
      const nextState = { ...state, ...stateUpdates };
      const stack = buildMenstrualHealthNavigationStack(nextState);
      const currentIdx = stack.indexOf("CycleStatus");
      const nextRoute = stack[currentIdx + 1];
      if (nextRoute) {
        navigation.navigate(nextRoute);
      }
    }
  };

  const handleSave = async () => {
    if (!state.cycleStatus) return;
    await save();
    const stack = buildMenstrualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("CycleStatus");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.menstrual_health.cycle_status_title")}
      subtitle={t("med_history.gynae.menstrual_health.cycle_status_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!state.cycleStatus}
      hideSaveButton={!state.cycleStatus}
      saveLabel={t("med_history.gynae.menstrual_health.continue")}
    >
      <View style={styles.optionsContainer}>
        {CYCLE_STATUS_OPTIONS.map((option) => (
          <RectangularOptionButton
            key={option.id}
            icon={option.icon}
            label={t(option.labelKey)}
            isSelected={state.cycleStatus === option.id}
            onPress={() => handleSelect(option.id)}
          />
        ))}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
});
